import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Support", href: "/support" },
    { name: "Contact", href: "/contact" },
  ];

  const contentDropdown = [
    { name: "Episodes", href: "/episodes" },
    { name: "Characters", href: "/characters" },
    { name: "Activities", href: "/activities" },
    { name: "Culture", href: "/culture" },
  ];

  const communityDropdown = [
    { name: "Partners", href: "/partners" },
    { name: "Our Sponsors", href: "/meet-our-sponsors" },
    { name: "Our Donors", href: "/meet-our-donors" },
  ];

  const allNavigation = [...navigation, ...contentDropdown, ...communityDropdown];

  const isActive = (path: string) => location.pathname === path;
  const isDropdownActive = (items: typeof contentDropdown) => 
    items.some(item => isActive(item.href));

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-lg border-b-4 border-teedee-blue">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center space-x-3 hover:scale-105 transition-transform duration-200"
            >
              <img
                src="/img/bobo-teedee-logo.png"
                alt="Bobo & Teedee®"
                className="h-12 w-auto"
              />
              <span className="text-2xl font-bold text-teedee-blue">
                Bobo & Teedee®
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:bg-sky-blue/20 hover:scale-105 ${
                  isActive(item.href)
                    ? "bg-teedee-blue text-white shadow-lg"
                    : "text-gray-700 hover:text-teedee-blue"
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Content Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:bg-sky-blue/20 hover:scale-105 ${
                    isDropdownActive(contentDropdown)
                      ? "bg-teedee-blue text-white shadow-lg"
                      : "text-gray-700 hover:text-teedee-blue"
                  }`}
                >
                  Content <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {contentDropdown.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link
                      to={item.href}
                      className={`w-full ${
                        isActive(item.href) ? "bg-muted font-medium" : ""
                      }`}
                    >
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Community Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:bg-sky-blue/20 hover:scale-105 ${
                    isDropdownActive(communityDropdown)
                      ? "bg-teedee-blue text-white shadow-lg"
                      : "text-gray-700 hover:text-teedee-blue"
                  }`}
                >
                  Community <ChevronDown className="ml-1 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {communityDropdown.map((item) => (
                  <DropdownMenuItem key={item.name} asChild>
                    <Link
                      to={item.href}
                      className={`w-full ${
                        isActive(item.href) ? "bg-muted font-medium" : ""
                      }`}
                    >
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(!isOpen)}
              className="text-teedee-blue hover:bg-sky-blue/20"
              aria-expanded="false"
              aria-label="Toggle navigation menu"
            >
              {isOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border-2 border-sky-blue/20 mt-2">
              {allNavigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-4 py-3 rounded-lg text-base font-medium transition-all duration-200 hover:bg-sky-blue/20 hover:scale-105 ${
                    isActive(item.href)
                      ? "bg-teedee-blue text-white shadow-lg"
                      : "text-gray-700 hover:text-teedee-blue"
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
